import { useCallback, useMemo } from 'react';
import type { ViewModelId } from 'balus-common';
import type { ViewEntity } from '@view-model/domain/view';
import { useAtomValue, useSetAtom } from 'jotai';
import { viewsAtom, workspacesAtom } from './SidebarTreeStore/sidebarTreeRecordsState';
import { setScrolledViewModelAtom, sidebarTreeContainerRefAtom } from './SidebarTreeStore/sidebarTreeControlState';
import { openViewModelIdsAtom, toggleViewModelOpenAtom } from './SidebarTreeStore/sidebarTreeOpenState';
import type { Workspace } from '@workspace/domain/workspace';

type Return = {
  isOpen: boolean;
  handleToggleOpen: () => void;
  sortedViews: ViewEntity[];
  workspace: Workspace;
  resetScrollViewModel: () => void;
  sidebarTreeContainerRef: React.RefObject<HTMLDivElement> | null;
};

export const useSidebarViewModel = (viewModelId: ViewModelId, workspaceId: string): Return => {
  const workspaces = useAtomValue(workspacesAtom);
  const views = useAtomValue(viewsAtom);
  const sidebarTreeContainerRef = useAtomValue(sidebarTreeContainerRefAtom);
  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);

  const openViewModelIds = useAtomValue(openViewModelIdsAtom);
  const toggleViewModelOpen = useSetAtom(toggleViewModelOpenAtom);

  const workspace = useMemo(() => workspaces[workspaceId], [workspaces, workspaceId]);
  const isOpen = !!openViewModelIds[viewModelId];
  const sortedViews = useMemo(
    () => Object.values(views[viewModelId] || {}).sort((a, b) => a.compareTo(b)),
    [views, viewModelId]
  );

  const handleToggleOpen = useCallback(() => {
    toggleViewModelOpen(viewModelId);
  }, [toggleViewModelOpen, viewModelId]);

  const resetScrollViewModel = useCallback(() => {
    setScrolledViewModel('');
  }, [setScrolledViewModel]);

  return {
    isOpen,
    handleToggleOpen,
    sortedViews,
    workspace,
    resetScrollViewModel,
    sidebarTreeContainerRef,
  };
};
